<template>
  <div class="variations-table">
    <VcTable
      :items="variations"
      :columns="columns"
      :sort="sort"
      :loading="fetching"
      :page="pageNumber"
      :pages="pagesCount"
      :hide-default-footer="!pagesCount"
      @header-click="applySorting"
      @page-changed="changePage"
    >
      <template #desktop-skeleton>
        <tr v-for="rowIndex in 6" :key="rowIndex" class="variations-table__row even:bg-neutral-50">
          <td v-for="columnIndex in columns.length" :key="columnIndex" class="p-5">
            <div class="h-6 animate-pulse bg-neutral-200" />
          </td>
        </tr>
      </template>

      <template #desktop-body>
        <tr v-for="(variation, variationIndex) in variations" :key="variation.code" class="variations-table__row">
          <td class="variations-table__col variations-table__col--actions">
            <VcProductActions :direction="'horizontal'">
              <AddToList :product="variation" />
              <AddToCompareCatalog v-if="$cfg.product_compare_enabled" :product="variation" />
            </VcProductActions>
          </td>
          <td class="variations-table__col variations-table__col--title">
            <VcProductTitle :lines-number="2" :to="getRoute(variation)" class="text-lg">
              {{ getProductName(variation, false) }}
              <div class="text-sm font-normal mt-1 clear-both">
                <span v-if="variation.code">{{ variation.code }}</span>
                <span v-if="variation.code" class="mx-1">|</span>
                <span> {{ getCustomPropertyValue(variation.properties, CUSTOM_PROPERTY_GROUP_MATERIAL) }} </span>
              </div>

              <span class="flex w-full text-base font-normal mt-1">{{}}</span>
            </VcProductTitle>
          </td>

          <td
            v-for="(property, index) in productProperties"
            :key="index"
            class="variations-table__col variations-table__col--property"
          >
            {{ property.values[variationIndex] }}
          </td>

          <!-- Innovadis custom: only show stock to authenticated users -->
          <td v-if="isAuthenticated" class="variations-table__col variations-table__col--in-stock">
            <VcBadge
              v-if="variation.availabilityData?.isInStock"
              color="success"
              size="xs"
              variant="outline-dark"
              class="variations-table__chip"
              truncate
              rounded
            >
              <template v-if="variation.availabilityData?.availableQuantity > 0">
                {{ getStockQuantity(variation) }}
              </template>

              <template v-else>
                {{ $t("common.labels.in_stock") }}
              </template>
            </VcBadge>

            <VcBadge
              v-else
              size="xs"
              variant="outline-dark"
              color="danger"
              class="variations-table__chip"
              truncate
              rounded
            >
              0
            </VcBadge>
          </td>

          <td class="variations-table__col variations-table__col--price">
            <InnoProductPrice
              :list-price="variation.price.list"
              :actual-price="variation.price.actual"
              :sales-unit-text="getProductSalesUnitText(variation)"
              align="end"
            />
          </td>

          <td class="variations-table__col variations-table__col--quantity overflow-hidden">
            <AddToCart
              :model-value="mappedLineItems[variation.id]?.quantity ?? 0"
              :product="variation"
              :name="variation.id"
              :disabled="
                // Innovadis custom: no stock check: !variation.availabilityData?.isInStock ||
                !variation.availabilityData?.isAvailable || !variation.availabilityData?.isBuyable
              "
              :error="!!getItemErrors(variation)"
              :timeout="DEFAULT_DEBOUNCE_IN_MS"
              :validate-on-mount="false"
              :pack-size="variation.packSize"
              :quantity-operators="$cfg.quantity_operators_enabled"
              :min-quantity="variation.minQuantity"
              :max-quantity="variation.maxQuantity"
              :available-quantity="variation.availabilityData?.availableQuantity"
              :is-in-stock="variation.availabilityData?.isInStock"
              :is-active="variation.availabilityData?.isActive"
              :is-available="variation.availabilityData?.isAvailable"
              :is-buyable="variation.availabilityData?.isBuyable"
              @update:model-value="changeCart(variation, $event)"
              @update:validation="handleClientValidation(variation.id, $event)"
            >
              <template v-if="!!getItemErrors(variation)" #append>
                <VcTooltip placement="bottom-end">
                  <template #trigger>
                    <VcIcon class="variations-table__quantity-icon" name="warning" />
                  </template>

                  <template #content>
                    <div class="w-max rounded-sm bg-additional-50 px-3.5 py-1.5 text-xs text-danger">
                      <div v-for="(error, index) in getItemErrors(variation)" :key="index">
                        {{ error }}
                      </div>
                    </div>
                  </template>
                </VcTooltip>
              </template>
            </AddToCart>

            <CountInCart :product-id="variation.id" class="variations-table__in-cart" />
          </td>
        </tr>
      </template>
    </VcTable>
  </div>
</template>

<script setup lang="ts">
import { flatten, sortBy, uniqBy } from "lodash";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { PropertyType } from "@/core/api/graphql/types";
import { useErrorsTranslator, useHistoricalEvents } from "@/core/composables";
import { useAnalyticsUtils } from "@/core/composables/useAnalyticsUtils";
import { MAX_DISPLAY_IN_STOCK_QUANTITY } from "@/core/constants";
import { getPropertyValue, getPropertiesGroupedByName, getProductRoute } from "@/core/utilities";
import { DEFAULT_DEBOUNCE_IN_MS } from "@/shared/cart";
import { useShortCart } from "@/shared/cart/composables";
import { CUSTOM_PROPERTIES_FOR_VARIATION_TABLE, CUSTOM_PROPERTY_GROUP_MATERIAL } from "@/shared/common/constants";
import { getCustomPropertyValue, getProductSalesUnitText, getProductName } from "@/core/utilities/innovadis";
import { AddToCompareCatalog } from "@/shared/compare";
import { AddToList } from "@/shared/wishlists";
import { AddToCart } from "@/shared/cart";
import { useUser } from "@/shared/account";
import CountInCart from "../../count-in-cart.vue";
import InnoProductPrice from "@/ui-kit//components/molecules/innovadis/inno-product-price.vue";
import type { Product, ShortLineItemFragment, ValidationErrorType } from "@/core/api/graphql/types";
import type { ISortInfo } from "@/core/types";
import { useRouter } from "vue-router";

interface IEmits {
  (event: "applySorting", item: ISortInfo): void;
  (event: "changePage", page: number): void;
}

interface IProps {
  variations: Product[];
  sort: ISortInfo;
  fetching: boolean;
  pageNumber?: number;
  pagesCount?: number;
}

interface IProductProperties {
  name: string;
  label: string;
  values: string[];
}

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const { isAuthenticated } = useUser();

const { t } = useI18n();
const { cart, addToCart, changeItemQuantity } = useShortCart();
const { localizedItemsErrors: serverValidationErrors, setErrors } =
  useErrorsTranslator<ValidationErrorType>("validation_error");
const { trackAddItemToCart } = useAnalyticsUtils();
const { pushHistoricalEvent } = useHistoricalEvents();

const clientValidation = ref<Record<string, { isValid: boolean; messages?: string[] }>>({});

const variations = computed(() => props.variations);
const productProperties = computed<IProductProperties[]>(() => {
  const properties: IProductProperties[] = [];
  const propertiesCombined = flatten(variations.value.map((variation) => getProperties(variation)));

  const names = uniqBy(
    propertiesCombined
      .filter((prop) => CUSTOM_PROPERTIES_FOR_VARIATION_TABLE.includes(prop.name)) // Innovadis custom: Only use properties from CUSTOM_PROPERTIES_FOR_VARIATION_TABLE as columns and format label
      .map((prop) => {
        return {
          name: prop.name,
          label: prop.label
            ? prop.label.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase()) // Capitalize only the first character
            : prop.label,
        };
      }),
    "name",
  );

  names.forEach(({ name, label }) => {
    properties.push({
      name,
      label,
      values: variations.value.map((variation) => {
        const property = variation.properties.find((item) => item.name === name);
        return property ? (getPropertyValue(property) ?? "\u2013") : "\u2013";
      }),
    });
  });

  return properties;
});

const propertiesTitles = computed<ITableColumn[]>(() =>
  productProperties.value.map((item) => ({
    id: item.name,
    // show translation if it is available, else label
    title:
      t(`shared.catalog.product_details.properties.property_${item.label}`) ===
      `shared.catalog.product_details.properties.property_${item.label}`
        ? item.label
        : t(`shared.catalog.product_details.properties.property_${item.label}`),
    sortable: true,
    align: "center",
    classes: "min-w-24 w-24 break-words",
  })),
);

const columns = computed<ITableColumn[]>(() => {
  const baseColumns: ITableColumn[] = [
    {
      id: "actions",
      sortable: false,
      classes: "w-[90px]",
    },
    {
      id: "name",
      title: t("shared.catalog.product_details.variations.columns.item"),
      sortable: true,
      classes: "min-w-52",
    },
    ...propertiesTitles.value,
  ];

  // Innovadis custom: only show stock to authenticated users
  if (isAuthenticated.value) {
    baseColumns.push({
      id: "instock_quantity",
      title: t("shared.catalog.product_details.variations.columns.stock"),
      align: "center",
      sortable: true,
      classes: "min-w-[4.375rem] w-[4.375rem]",
    });
  }

  baseColumns.push(
    {
      id: "price",
      title: t("shared.catalog.product_details.variations.columns.price"),
      align: "right",
      sortable: true,
      classes: "min-w-32 w-32",
    },
    {
      id: "quantity",
      title: t("shared.catalog.product_details.variations.columns.quantity"),
      align: "left",
      classes: "min-w-72 w-72",
    },
  );

  return baseColumns;
});

const mappedLineItems = computed(() => {
  const mapped: Record<string, ShortLineItemFragment | undefined> = {};
  variations.value?.forEach((variation) => (mapped[variation.id] = getLineItem(variation)));
  return mapped;
});

function getStockQuantity(variation: Product) {
  return variation.availabilityData.availableQuantity &&
    variation.availabilityData.availableQuantity > MAX_DISPLAY_IN_STOCK_QUANTITY
    ? `${MAX_DISPLAY_IN_STOCK_QUANTITY}+`
    : variation.availabilityData.availableQuantity.toString();
}

function getProperties(variation: Product) {
  return Object.values(
    getPropertiesGroupedByName(sortBy(variation.properties, ["displayOrder", "name"]) ?? [], PropertyType.Variation),
  );
}

// Innovadis custom: use slug from back-end before id (or a wrong url will be shown), url decode (or a 404 page will appear) and remove culture prefix (or it will be there twice)
function getRoute(item: Product) {
  const router = useRouter();

  if (item?.id && item?.slug) {
    const rawRoute = getProductRoute(item.slug || item.id);

    const resolved = router.resolve(rawRoute);

    let decodedHref = decodeURIComponent((resolved.params.productId as string) || "");

    // Zorg dat er een leading slash staat
    if (!decodedHref.startsWith("/")) {
      decodedHref = "/" + decodedHref;
    }

    return decodedHref;
  }

  return "";
}

function getLineItem(variation: Product): ShortLineItemFragment | undefined {
  return cart.value?.items?.find((item) => item.productId === variation.id);
}

function getItemErrors(variation: Product) {
  if (clientValidation.value[variation.id]?.isValid === false) {
    return clientValidation.value[variation.id]?.messages;
  }

  if (serverValidationErrors.value[variation.id]) {
    return serverValidationErrors.value[variation.id];
  }

  const lineItem = getLineItem(variation);
  if (lineItem?.id) {
    return serverValidationErrors.value[lineItem.id];
  }
}

async function changeCart(variation: Product, quantity: number) {
  if (!clientValidation.value[variation.id]?.isValid) {
    return;
  }
  const lineItem = getLineItem(variation);

  if (lineItem) {
    await changeItemQuantity(lineItem.id, quantity);
  } else {
    await addToCart(variation.id, quantity);
    trackAddItemToCart(variation, quantity);
    void pushHistoricalEvent({ eventType: "addToCart", productId: variation.id });
  }
}

function applySorting(sortInfo: ISortInfo): void {
  emit("applySorting", sortInfo);
}

function changePage(page: number): void {
  emit("changePage", page);
}

function handleClientValidation(id: string, validation: { isValid: true } | { isValid: false; errorMessage: string }) {
  if (!validation.isValid) {
    clientValidation.value[id] = { isValid: false, messages: [validation.errorMessage] };
  } else {
    clientValidation.value[id] = { isValid: true };
  }
}

watchEffect(() => {
  if (cart.value?.validationErrors) {
    setErrors(cart.value?.validationErrors);
  }
});
</script>

<style lang="scss">
.variations-table {
  @apply overflow-x-auto rounded border;

  &__row {
    @apply even:bg-neutral-50 hover:bg-neutral-100;
  }

  &__col {
    &--actions {
      @apply py-2.5 ps-4;
    }

    &--title {
      @apply py-2.5 ps-4;
    }

    &--property {
      @apply px-2 py-2.5 text-center break-words;
    }

    &--in-stock {
      @apply px-2 py-3;
    }

    &--price {
      @apply p-3;

      .vc-price-display {
        @apply text-lg;
      }

      .vc-product-price__price-unit {
        @apply text-xs;
      }

      .vc-product-price__sales-unit {
        @apply text-xxs font-normal text-[--color-neutral-500];
      }
    }

    &--quantity {
      @apply p-3;
    }
  }

  &__chip {
    @apply w-full;
  }

  &__quantity-icon {
    @apply me-3 size-4 fill-danger;
  }

  &__in-cart {
    @apply mt-1.5;
  }
}
</style>
